import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Box,
  Divider,
  Button,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ArrowBack as BackIcon,
  Security as SecurityIcon,
  Settings as SystemIcon,
  Dashboard as OverviewIcon,
  Business as CompanyIcon,
  Email as EmailIcon,
  NotificationsActive as EventsIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { usePermissions } from '../../contexts/PermissionContext';
import ThemeToggle from '../../components/ThemeToggle';
import NotificationsBell from '../../components/NotificationsBell';
import HeaderChatButton from '../../components/HeaderChatButton';

const drawerWidth = 280;
const collapsedDrawerWidth = 64;

const ConfigurationLayout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = usePermissions();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDesktopDrawerToggle = () => {
    setDesktopOpen(!desktopOpen);
  };

  const handleBackToMain = () => {
    navigate('/dashboard');
  };

  const configMenuItems = [
    {
      text: 'Tableau de bord',
      icon: <OverviewIcon />,
      path: '/configuration',
      exact: true,
      moduleName: null,
      miniModuleName: null,
    },
    {
      text: 'Serveurs SMTP',
      icon: <EmailIcon />,
      path: '/configuration/smtp-settings',
      moduleName: 'Admin',
      miniModuleName: 'SMTP Settings',
    },
    {
      text: 'Événements Email',
      icon: <EventsIcon />,
      path: '/configuration/email-events',
      moduleName: 'Admin',
      miniModuleName: 'Email Events',
    },
    {
      text: 'Historique des Emails',
      icon: <HistoryIcon />,
      path: '/configuration/email-history',
      moduleName: 'Admin',
      miniModuleName: 'Email History',
    },
    {
      text: 'Sécurité',
      icon: <SecurityIcon />,
      path: '/configuration/security',
      moduleName: null,
      miniModuleName: null,
      comingSoon: true
    },
    {
      text: 'Paramètres Système',
      icon: <SystemIcon />,
      path: '/configuration/system',
      moduleName: null,
      miniModuleName: null,
      comingSoon: true
    },
    {
      text: 'Informations Entreprise',
      icon: <CompanyIcon />,
      path: '/configuration/company',
      moduleName: null,
      miniModuleName: null,
      comingSoon: true
    },
  ];

  // Filter menu items based on permissions - hide items user doesn't have access to
  const visibleMenuItems = configMenuItems.filter((item) => {
    // Coming soon items are always shown (but disabled)
    if (item.comingSoon) return true;
    // Items without permission check are always shown
    if (!item.miniModuleName || !item.moduleName) return true;
    // Check permission for items that require it
    return hasPermission(item.moduleName, 'view', item.miniModuleName);
  });

  // Mark coming soon items as disabled
  const menuItemsWithPermissions = visibleMenuItems.map((item) => {
    if (item.comingSoon) return { ...item, hasAccess: false };
    return { ...item, hasAccess: true };
  });

  const isActiveRoute = (path: string, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const drawer = (
    <div>
         <Toolbar>
           {desktopOpen ? (
             <Button
               fullWidth
               startIcon={<BackIcon />}
               onClick={handleBackToMain}
               variant="outlined"
               size="small"
               sx={{ whiteSpace: 'nowrap', overflow: 'hidden', justifyContent: 'flex-start' }}
             >
               Retour au Menu Principal
             </Button>
           ) : (
             <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
               <IconButton
                 onClick={handleBackToMain}
                 size="small"
               >
                 <BackIcon />
               </IconButton>
             </Box>
           )}
         </Toolbar>
         <Divider />
      
      <List>
        {menuItemsWithPermissions.map((item) => {
          const isDisabled = item.comingSoon || !item.hasAccess;
          const isActive = !isDisabled && isActiveRoute(item.path, item.exact);

          const tooltipTitle = !item.hasAccess && !item.comingSoon ? 'Accès refusé' : '';

          return (
            <ListItem key={item.text} disablePadding>
              <Tooltip title={tooltipTitle} placement="right" arrow>
                <span style={{ width: '100%' }}>
                  <ListItemButton
                    selected={isActive}
                    onClick={!isDisabled ? () => navigate(item.path) : undefined}
                    disabled={isDisabled}
                    sx={{
                      minHeight: 48,
                      justifyContent: desktopOpen ? 'initial' : 'center',
                      px: desktopOpen ? 2.5 : 0,
                      '&.Mui-selected': {
                        backgroundColor: '#42A5F5',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: '#1976D2',
                        },
                      },
                      '&.Mui-disabled': {
                        opacity: 0.6,
                        cursor: 'not-allowed',
                        '& .MuiListItemIcon-root': {
                          color: 'text.disabled',
                        },
                        '& .MuiListItemText-root': {
                          color: 'text.disabled',
                        },
                      },
                      cursor: !isDisabled ? 'pointer' : 'not-allowed',
                    }}
                  >
                <ListItemIcon sx={{
                  minWidth: 0,
                  mr: desktopOpen ? 3 : 0,
                  ml: desktopOpen ? 0 : 0,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  color: isActive
                    ? 'white'
                    : !isDisabled
                      ? 'inherit'
                      : 'text.disabled'
                }}>
                  {item.icon}
                </ListItemIcon>
                {desktopOpen && (
                  <ListItemText
                    primary={item.text}
                    secondary={item.comingSoon ? 'Bientôt disponible' : undefined}
                    primaryTypographyProps={{
                      fontWeight: isActive ? 600 : 400,
                      color: !isDisabled ? 'inherit' : 'text.disabled',
                    }}
                    secondaryTypographyProps={{
                      fontSize: '0.75rem',
                      fontStyle: 'italic',
                    }}
                  />
                )}
                  </ListItemButton>
                </span>
              </Tooltip>
            </ListItem>
          );
        })}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: {
            sm: desktopOpen ? `calc(100% - ${drawerWidth}px)` : `calc(100% - ${collapsedDrawerWidth}px)`
          },
          ml: {
            sm: desktopOpen ? `${drawerWidth}px` : `${collapsedDrawerWidth}px`
          },
          bgcolor: '#1976D2',
          transition: 'width 0.3s ease, margin 0.3s ease',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <IconButton
            color="inherit"
            aria-label="toggle drawer"
            edge="start"
            onClick={handleDesktopDrawerToggle}
            sx={{ mr: 2, display: { xs: 'none', sm: 'block' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Module Configuration
          </Typography>
          <HeaderChatButton />
          <NotificationsBell />
          <ThemeToggle />
        </Toolbar>
      </AppBar>
      
      <Box
        component="nav"
        onMouseEnter={() => {
          if (!desktopOpen) setDesktopOpen(true);
        }}
        sx={{
          width: { sm: desktopOpen ? drawerWidth : collapsedDrawerWidth },
          flexShrink: { sm: 0 },
          transition: 'width 0.3s ease'
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth 
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: desktopOpen ? drawerWidth : collapsedDrawerWidth,
              transition: 'width 0.3s ease',
              overflowX: 'hidden',
            },
          }}
          open={desktopOpen}
        >
          {drawer}
        </Drawer>
      </Box>
      
      <Box
        component="main"
        onClick={() => {
          if (desktopOpen) setDesktopOpen(false);
        }}
        sx={{
          flexGrow: 1,
          py: 3,
          px: 2,
          width: {
            sm: desktopOpen ? `calc(100% - ${drawerWidth}px)` : `calc(100% - ${collapsedDrawerWidth}px)`
          },
          ml: {
            sm: 0
          },
          transition: 'width 0.3s ease, margin-left 0.3s ease',
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};

export default ConfigurationLayout;